/**
 * Canonical ``lot.lot_model`` (protocol §2.3): ``XXX-Y``, ``XXX-YY``, or ``"0"``.
 * Station firmware ≥ v1.4.0 publishes hyphen SKUs passthrough; no packed-digit rewrite.
 * ``402-1`` and ``402-01`` are distinct (no revision padding).
 */

export type LotModelEquipmentProfile = 'refill' | 'washing' | 'charging' | 'unknown'

const LOT_MODEL_ZERO = '0'
const LOT_MODEL_SKU_RE = /^\d{3}-\d{1,2}$/

const REFILL_SKUS = new Set(['402-01', '402-0', '402-1'])
const WASHING_SKUS = new Set(['305-01', '313-01', '401-01', '401-0', '401-1'])
const CHARGING_SKUS = new Set(['307-01', '319-01', '403-01', '403-0', LOT_MODEL_ZERO])
/** Dock SKUs that expose STEPS 17–19 (wash dirty tank + start/stop charging). */
export const EXTENDED_DOCK_STEPS_SKUS = new Set(['401-01', '401-0', '401-1'])
export const EXTENDED_DOCK_STEP_COMMANDS = new Set([17, 18, 19])
/** Washing v2: STEPS only CANCEL / FILL_WATER_TANK / WASH_SUCTION_PIPE. */
export const LIMITED_WASHING_V2_STEPS_SKUS = new Set(['313-01'])
export const LIMITED_WASHING_V2_STEP_COMMANDS = new Set([1, 5, 6])
/** FMS-CTRL CLEAR_OVERPRESSURE — pump pressure sensors. */
export const CLEAR_OVERPRESSURE_LOT_SKUS = new Set(['305-01', '401-01', '401-0', '401-1'])

export const REFILL_DRAIN_SKUS = new Set(['402-1'])
/** STEPS DIRTY_WATER_DRAIN — only 402-1. */
export const DIRTY_WATER_DRAIN_STEP_COMMAND = 20

/** Same product, different wire strings. ``402-1`` is not an alias of ``402-0``. */
const LOT_MODEL_ALIASES: Record<string, string> = {
  '401-01': '401-0',
  '402-01': '402-0',
  '403-01': '403-0',
}

const SKU_LABELS: Record<string, string> = {
  '305-01': 'Washing',
  '313-01': 'Washing v2',
  '401-01': 'Washing (Sweeper / Combo)',
  '401-0': 'Washing (Sweeper / Combo)',
  '401-1': 'Washing (Omni)',
  '307-01': 'Charging',
  '319-01': 'Charging',
  '403-01': 'Charging',
  '403-0': 'Charging',
  '402-01': 'Refill',
  '402-0': 'Refill',
  '402-1': 'Refill with drainage',
  '0': 'Charge',
}

/** Trim wire/catalog SKU; keep hyphen forms as-is. */
export function canonicalizeLotModel(value?: string | null): string {
  const text = (value || '').trim()
  if (!text) return ''
  if (text === LOT_MODEL_ZERO) return LOT_MODEL_ZERO
  if (LOT_MODEL_SKU_RE.test(text)) return text
  return text
}

/** @deprecated use canonicalizeLotModel */
export function normalizeWireLotModel(value?: string | null): string {
  return canonicalizeLotModel(value)
}

export function lotModelEquipmentProfile(lotModel?: string | null): LotModelEquipmentProfile {
  const sku = canonicalizeLotModel(lotModel)
  if (REFILL_SKUS.has(sku)) return 'refill'
  if (WASHING_SKUS.has(sku)) return 'washing'
  if (CHARGING_SKUS.has(sku)) return 'charging'
  return 'unknown'
}

export function isRefillLotModel(lotModel?: string | null): boolean {
  return lotModelEquipmentProfile(lotModel) === 'refill'
}

export function supportsExtendedDockSteps(lotModel?: string | null): boolean {
  return EXTENDED_DOCK_STEPS_SKUS.has(canonicalizeLotModel(lotModel))
}

export function supportsLimitedWashingV2Steps(lotModel?: string | null): boolean {
  return LIMITED_WASHING_V2_STEPS_SKUS.has(canonicalizeLotModel(lotModel))
}

export function supportsDirtyWaterDrainSteps(lotModel?: string | null): boolean {
  return REFILL_DRAIN_SKUS.has(canonicalizeLotModel(lotModel))
}

export function supportsClearOverpressure(lotModel?: string | null): boolean {
  return CLEAR_OVERPRESSURE_LOT_SKUS.has(canonicalizeLotModel(lotModel))
}

/** Washing and refill lots have maintenance stages; charging-only lots do not. */
export function supportsMaintenanceStages(lotModel?: string | null): boolean {
  const profile = lotModelEquipmentProfile(lotModel)
  return profile === 'washing' || profile === 'refill'
}

export function lotModelProfileId(value?: string | null): string {
  const sku = canonicalizeLotModel(value)
  return LOT_MODEL_ALIASES[sku] || sku
}

export function lotModelsMatch(a?: string | null, b?: string | null): boolean {
  const ca = lotModelProfileId(a)
  const cb = lotModelProfileId(b)
  if (!ca && !cb) return true
  if (!ca || !cb) return false
  return ca === cb
}

const SWEEPER_COMBO_ROBOT_TYPES = ['Sweeper', 'Combo'] as const

/**
 * Restricted washing SKUs. Key is profile id (`401-01` collapses to `401-0`).
 * `401-1` stays distinct (Omni only). Unknown SKUs are unrestricted (`null`).
 */
const WASHING_LOT_ROBOT_TYPES: Record<string, readonly string[]> = {
  '401-0': SWEEPER_COMBO_ROBOT_TYPES,
  '305-01': SWEEPER_COMBO_ROBOT_TYPES,
  '313-01': SWEEPER_COMBO_ROBOT_TYPES,
  '401-1': ['ComboOmni'],
}

/** Allowed robot type codes, or `null` when the SKU is not restricted. */
export function compatibleRobotTypesForLotModel(lotModel?: string | null): readonly string[] | null {
  const profile = lotModelProfileId(lotModel)
  if (!profile) return null
  return WASHING_LOT_ROBOT_TYPES[profile] ?? null
}

/**
 * Unrestricted SKUs always match. A restricted SKU with an empty robot type
 * also matches (the caller has not chosen a robot yet).
 */
export function isWashingLotCompatibleWithRobotType(
  lotModel?: string | null,
  robotType?: string | null
): boolean {
  const allowed = compatibleRobotTypesForLotModel(lotModel)
  if (!allowed) return true
  const code = (robotType || '').trim()
  if (!code) return true
  return allowed.includes(code)
}

/** Add Service Point menu: Unknown / empty scenario type does not hide docks. */
export function washingLotVisibleForScenarioRobot(
  lotModel?: string | null,
  scenarioRobotType?: string | null
): boolean {
  const code =
    scenarioRobotType && scenarioRobotType !== 'Unknown' ? scenarioRobotType : undefined
  return isWashingLotCompatibleWithRobotType(lotModel, code)
}

export function lotModelDisplayLabel(lotModel?: string | null): string {
  const sku = canonicalizeLotModel(lotModel)
  if (sku && SKU_LABELS[sku]) return SKU_LABELS[sku]
  const trimmed = lotModel?.trim()
  return trimmed || 'Unknown'
}

export function findLotModelByNumber<T extends { model_number: string }>(
  lotModels: T[],
  lotModel?: string | null
): T | undefined {
  if (!lotModel) return undefined
  return lotModels.find(m => lotModelsMatch(m.model_number, lotModel))
}
